import {
  type UserProfileFormValues,
  userProfileFormSchema,
} from "@/features/management-settings/forms/schemas/user-profile-form.schema";
import type { UpdateUserProfileRequestDto } from "@/spz-api/model";
import { useGetMe, useUsersControllerUpdateProfile } from "@/spz-api/user/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { USER_PROFILE_FORM_DEFAULT_VALUES } from "../forms/constants/user-profile-form.constants";
import { useUploadPictureUrlMutation } from "./mutations/use-upload-picture-url.mutation";

interface UseUpdateUserFormProps {
  initialValues?: Partial<UserProfileFormValues>;
  onSuccess?: (data: UserProfileFormValues) => void;
  onError?: (error: Error) => void;
}

export const useUpdateUserForm = ({
  onSuccess,
  onError,
}: UseUpdateUserFormProps = {}) => {
  const { t } = useTranslation();
  const { mutateAsync: updateProfile, isPending } =
    useUsersControllerUpdateProfile();
  const { data: me } = useGetMe();

  const userProfileSchema = userProfileFormSchema(t);

  const form = useForm<UserProfileFormValues>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: USER_PROFILE_FORM_DEFAULT_VALUES,
  });

  const uploadPictureUrlMutation = useUploadPictureUrlMutation();

  const onSubmit = async (data: UserProfileFormValues) => {
    try {
      if (!me?.id) {
        throw new Error("User ID not found");
      }

      let attachmentId: string | undefined;
      if (data.file instanceof File) {
        const result = await uploadPictureUrlMutation.mutateAsync({
          file: data.file,
        });
        attachmentId = result?.id;
      }

      const requestDto: UpdateUserProfileRequestDto = {
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        preferredLanguage: data.preferredLanguage,
        deleteAvatar: Boolean(data.deleteAvatar),
        profilePictureId: attachmentId,
      };

      await updateProfile({
        data: requestDto,
      });

      onSuccess?.(data);
    } catch (error) {
      console.error("Form submission error:", error);
      onError?.(
        error instanceof Error ? error : new Error("Unknown error occurred"),
      );
    }
  };

  return {
    form,
    isLoading: isPending,
    isDirty: form.formState.isDirty,
    onSubmit,
  };
};
